import { useEffect, useState } from "react"

import { Send } from "@mui/icons-material"
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material"

import {
  handleApiError,
  purchaseReceiptsService,
  type SendPurchaseReceiptResult,
} from "../../api"

type SendPurchaseReceiptModalProps = {
  open: boolean
  onClose: () => void
  orderId: string
  customerEmail: string
  onSent: (result: SendPurchaseReceiptResult) => void
}

const isOtherAddress = (options: { to: string; customerEmail: string }) =>
  options.to.trim() !== "" &&
  options.to.trim().toLowerCase() !== options.customerEmail.toLowerCase()

export function SendPurchaseReceiptModal({
  open,
  onClose,
  orderId,
  customerEmail,
  onSent,
}: SendPurchaseReceiptModalProps) {
  const [to, setTo] = useState("")
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    if (open) {
      setTo("")
      setError("")
    }
  }, [open])

  const sendsPreview = isOtherAddress({ to, customerEmail })

  const handleSend = async () => {
    try {
      setIsSending(true)
      setError("")
      const result = await purchaseReceiptsService.sendForOrder(
        orderId,
        sendsPreview ? { to: to.trim() } : {},
      )
      onSent(result)
      onClose()
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setIsSending(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send Purchase Receipt</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Builds the receipt from live Stripe, PayPal or FF Billing data and sends it now, even
          while the purchase receipt flags are off. Sent to the customer (
          <strong>{customerEmail}</strong>), it is recorded as their receipt and
          will not be sent again. Sent to another address, it is only a preview
          and the customer still gets the real receipt later.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          label="Send to another address (optional)"
          type="email"
          fullWidth
          size="small"
          value={to}
          onChange={(event) => setTo(event.target.value)}
          disabled={isSending}
          helperText={
            sendsPreview
              ? "Preview only: nothing is recorded for this order."
              : "Empty: send to the customer and record it."
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={isSending}
          startIcon={isSending ? <CircularProgress size={16} /> : <Send />}
        >
          {sendsPreview ? "Send Preview" : "Send to Customer"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
